import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { tournamentApi } from '../services/api'
import { mdToHtml } from '../components/MarkdownField'
import { displayPhone } from '../utils/phone'
import SplitCountdown from '../components/SplitCountdown'

/**
 * Public, read-only customer view of a Custom-Trip QUOTE (CTQ).
 * Reached from the emailed link /quote/:token — the unguessable token is the
 * credential; no login. Shows the itinerary + price + validity. Accept/pay is
 * intentionally NOT here yet (lands with the payment work).
 *
 * CTQ feature branch — not deployed to production until the feature is done.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format(Number(n))

function fmtDay(v, lang) {
  if (!v) return ''
  const ymd = String(v).slice(0, 10)
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) } catch { return ymd }
}
function fmtLongDay(v, lang) {
  if (!v) return ''
  const ymd = String(v).slice(0, 10)
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }) } catch { return ymd }
}
// Full spelled-out date incl. year, e.g. "Wednesday, September 9, 2026".
function fmtFullDate(v, lang) {
  if (!v) return ''
  const ymd = String(v).slice(0, 10)
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) } catch { return ymd }
}
const ymd = (v) => (v ? String(v).slice(0, 10) : '')
function addDaysY(s, n) { const d = new Date(s + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
function daysBetweenY(a, b) { return Math.round((new Date(b + 'T00:00:00Z') - new Date(a + 'T00:00:00Z')) / 86400000) }
// Readable text colour (dark/white) for a coloured background.
function contrastText(hex) {
  const m = String(hex || '').replace('#', '')
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return '#fff'
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0f172a' : '#fff'
}
function fmtTee(tt) {
  if (!tt) return null
  return String(tt).replace(/(\d{1,2}):(\d{2})(?::\d{2})?/g, (_, h, mm) => { let hr = Number(h); const ap = hr >= 12 ? 'PM' : 'AM'; hr = hr % 12 || 12; return `${hr}:${mm} ${ap}` })
}
// Absolute deadline (epoch ms) → e.g. "Fri, Aug 14 at 10:46 AM".
function fmtDeadline(ms, lang) {
  if (!ms) return ''
  try { return new Date(ms).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' }
}
// Rough "time left" until an epoch-ms deadline.
function timeLeft(ms, fr) {
  if (!ms) return ''
  const diff = ms - Date.now()
  if (diff <= 0) return fr ? 'expiré' : 'expired'
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000)
  if (d >= 1) return fr ? `${d} j ${h} h restantes` : `${d}d ${h}h left`
  if (h >= 1) return fr ? `${h} h ${m} min restantes` : `${h}h ${m}m left`
  return fr ? `${m} min restantes` : `${m}m left`
}
// Live H:MM:SS / M:SS clock from seconds remaining.
function fmtClock(s) {
  if (s == null) return ''
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
}

export default function CustomerQuoteView({ preview = false }) {
  const { token, id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  // Change-request form state: per-item selection (itemId -> { mode, note }),
  // an overall comment, plus submit status.
  const [crSel, setCrSel] = useState({})
  const [crComment, setCrComment] = useState('')
  const [crBusy, setCrBusy] = useState(false)
  const [crErr, setCrErr] = useState(null)
  const [crDone, setCrDone] = useState(false)
  const [crTab, setCrTab] = useState('history') // 'history' | 'form' (when both show)
  // Accept & pay state
  const [acPlan, setAcPlan] = useState(null)
  const [acCard, setAcCard] = useState({ number: '', expiry: '', cvc: '' })
  const [acAutoPay, setAcAutoPay] = useState(true)   // deferred plans: save card + auto-pay future payments
  const [acBusy, setAcBusy] = useState(false)
  const [acStep, setAcStep] = useState(0)              // rotating reassurance message while acBusy
  const [acErr, setAcErr] = useState(null)
  const [acConflict, setAcConflict] = useState(null)   // { roundItemId, courseName, currentTeeTime, alternates }
  const [acEscalated, setAcEscalated] = useState(null) // { courseName }
  const [acNotice, setAcNotice] = useState(null)       // e.g. price-changed message
  const [acMode, setAcMode] = useState('together')     // 'together' (single payer) | 'split' (per golfer)
  // Manual pay of a scheduled balance/installment (non-auto-pay deferred plans).
  const [payOpenId, setPayOpenId] = useState(null)     // which scheduled row's card form is open
  const [payCard, setPayCard] = useState({ number: '', expiry: '', cvc: '' })
  const [payBusy, setPayBusy] = useState(false)
  const [payErr, setPayErr] = useState(null)

  useEffect(() => {
    const url = preview ? `/portal/reseller/quotes/${id}/preview` : `/public/quote/${token}`
    tournamentApi.get(url)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.status === 404 ? 'notfound' : (e?.response?.data?.message || e.message)))
      .finally(() => setLoading(false))
  }, [token, id, preview])

  // Resume an in-flight split after a page refresh: if the quote already has a
  // split collecting, default the Accept & pay card to the split tab (the panel
  // itself is seeded with the persisted roster + tokens).
  useEffect(() => {
    if (data && data.split && ['collecting', 'booking'].includes(data.split.status)) setAcMode('split')
  }, [data && data.split && data.split.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle a per-item request mode ('change' | 'remove'); clicking the active
  // one clears it. Keeps any typed note when switching between modes.
  function setItemMode(itemId, mode) {
    setCrDone(false)
    setCrSel(prev => {
      const cur = prev[itemId] || {}
      const next = { ...prev }
      if (cur.mode === mode) next[itemId] = { ...cur, mode: null }
      else next[itemId] = { ...cur, mode }
      return next
    })
  }
  function setItemNote(itemId, note) {
    setCrSel(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), note } }))
  }

  async function submitChangeRequest() {
    // A "change" with no note is meaningless ("change to what?") — usually an
    // accidental toggle. Drop those so an empty selection does nothing. "Remove"
    // needs no note.
    const requests = Object.entries(crSel)
      .filter(([, v]) => v && v.mode && !(v.mode === 'change' && !(v.note || '').trim()))
      .map(([itemId, v]) => ({ itemId: Number(itemId), kind: v.mode, note: v.mode === 'change' ? (v.note || '').trim() : '' }))
    if (requests.length === 0 && !crComment.trim()) { setCrErr('Add a note to a change, mark an item to remove, or write a comment.'); return }
    setCrBusy(true); setCrErr(null)
    try {
      const r = await tournamentApi.post(`/public/quote/${token}/change-request`, { requests, comment: crComment.trim() })
      setData(r.data)
      setCrSel({}); setCrComment(''); setCrDone(true); setCrTab('history')
    } catch (e) {
      setCrErr(e?.response?.data?.message || e.message)
    } finally { setCrBusy(false) }
  }

  // While an accept/pay is in flight the server re-checks the live hotel rate,
  // reserves the tee times, holds the card, books, and captures — which can take
  // up to ~a minute. Rotate reassurance so the customer knows it isn't hung.
  const AC_STEPS_EN = [
    'Confirming live hotel & tee-time rates…',
    'Reserving your rooms and tee times…',
    'Securely processing your payment…',
    'Finalizing your trip — almost done…',
  ]
  const AC_STEPS_FR = [
    'Confirmation des tarifs d’hôtel et de départ en temps réel…',
    'Réservation de vos chambres et départs…',
    'Traitement sécurisé de votre paiement…',
    'Finalisation de votre voyage — presque terminé…',
  ]
  useEffect(() => {
    if (!acBusy) { setAcStep(0); return }
    const id = setInterval(() => setAcStep(s => s + 1), 5000)
    return () => clearInterval(id)
  }, [acBusy])

  // Accept the quote + pay. The server holds the card, books the tee times, and
  // captures only if all succeed — so a 200 can also come back as a conflict
  // (a time was taken), an escalation (none left), or a price change.
  async function acceptAndPay(plan) {
    const card = { number: (acCard.number || '').trim(), expiry: (acCard.expiry || '').trim(), cvc: (acCard.cvc || '').trim() }
    if (!plan) { setAcErr('Choose a payment option.'); return }
    if (!card.number || !card.expiry || !card.cvc) { setAcErr('Enter your card details.'); return }
    setAcBusy(true); setAcErr(null); setAcConflict(null); setAcEscalated(null); setAcNotice(null)
    try {
      const r = await tournamentApi.post(`/public/quote/${token}/accept`, { plan, card, autoPay: plan !== 'full' && acAutoPay })
      const d = r.data
      setData(d) // refresh itinerary / total / status in every case
      if (d.ok) setAcCard({ number: '', expiry: '', cvc: '' })         // accepted -> confirmed state
      else if (d.conflict) setAcConflict({ roundItemId: d.roundItemId, courseName: d.courseName, currentTeeTime: d.currentTeeTime, alternates: d.alternates || [] })
      else if (d.escalated) setAcEscalated({ courseName: d.courseName })
      else if (d.priceChanged) setAcNotice(`Prices changed since this quote was sent — your new total is ${money(d.totals && d.totals.total, cur)}. Review the trip below and pay again.`)
    } catch (e) {
      setAcErr(e?.response?.data?.message || e.message)
    } finally { setAcBusy(false) }
  }

  // Manual pay of a scheduled balance/installment (single-payer, non-auto-pay).
  // The trip is already booked; this just collects the outstanding payment.
  async function payScheduled(paymentId) {
    if (payBusy) return
    const card = { number: (payCard.number || '').trim(), expiry: (payCard.expiry || '').trim(), cvc: (payCard.cvc || '').trim() }
    if (!card.number || !card.expiry || !card.cvc) { setPayErr(fr ? 'Détails de carte requis.' : 'Enter your card details.'); return }
    setPayBusy(true); setPayErr(null)
    try {
      const r = await tournamentApi.post(`/public/quote/${token}/pay-scheduled`, { paymentId, card })
      setData(r.data) // refreshes the payment schedule + status
      setPayOpenId(null); setPayCard({ number: '', expiry: '', cvc: '' })
    } catch (e) {
      setPayErr(e?.response?.data?.message || e.message)
    } finally { setPayBusy(false) }
  }

  // After a conflict: swap the round to the picked alternate time, then auto-retry.
  async function pickConflictAlternate(opt, plan) {
    if (!acConflict) return
    setAcBusy(true); setAcErr(null)
    try {
      const r = await tournamentApi.post(`/public/quote/${token}/round/${acConflict.roundItemId}/change-time`, { teeTime: opt.teeTime, ref: opt.ref })
      setData(r.data); setAcConflict(null)
    } catch (e) {
      setAcErr(e?.response?.data?.message || e.message); setAcBusy(false); return
    }
    setAcBusy(false)
    await acceptAndPay(plan) // retry the payment on the new time
  }

  if (loading) return <Centered><div style={{ color: '#64748b' }}>Loading…</div></Centered>
  if (err === 'notfound') return <Centered><Card><h2 style={{ margin: 0, fontSize: 20 }}>Quote not found</h2><p style={{ color: '#64748b' }}>This quote link isn’t available. It may have been withdrawn — please contact your travel provider.</p></Card></Centered>
  if (err) return <Centered><Card><p style={{ color: '#991b1b' }}>{err}</p></Card></Centered>
  if (!data) return null

  const { quote: q, brand, planner, items, totals: T, messages = [] } = data
  const cur = q.currency || 'CAD'
  const hotels = items.filter(i => i.type === 'hotel')
  const rounds = items.filter(i => i.type === 'round').sort((a, b) => (a.dayIndex - b.dayIndex) || String(a.teeTime).localeCompare(String(b.teeTime)))
  const customs = items.filter(i => i.type === 'custom')
  const expired = q.status === 'expired' || (q.validUntil && new Date(String(q.validUntil).slice(0, 10) + 'T23:59:59Z') < new Date())
  const accent = brand?.primaryColor || '#0f172a'
  const onAccent = contrastText(accent)

  // Change requests: the customer may ask for tweaks while the quote is live
  // (sent / change_requested) and not expired. In preview we render the UI so
  // the reseller sees it, but disabled. `requestable` items are the tangible
  // hotel stays + tee times (not custom line items).
  const canRequest = !!q.canRequestChanges && !expired
  const showRequestForm = canRequest && !preview
  const requestable = [
    ...hotels.map(h => ({ id: h.id, kind: 'HOTEL', color: '#7c3aed', title: h.hotelName || 'Hotel', sub: [h.roomType, h.checkInDate ? `${fmtDay(h.checkInDate)}${h.checkOutDate ? ` → ${fmtDay(h.checkOutDate)}` : ''}` : null].filter(Boolean).join(' · ') })),
    ...rounds.map(r => ({ id: r.id, kind: 'GOLF', color: '#16a34a', title: r.courseName || 'Golf', sub: [fmtDay(r.playDate), fmtTee(r.teeTime)].filter(Boolean).join(' · ') })),
  ].filter(x => x.id != null)
  const submittedRequests = messages.filter(m => m.isChangeRequest || m.authorType === 'reseller')
  // History (already-submitted requests + replies) and the request form each
  // show on their own; when BOTH are present we tab between them, defaulting to
  // the history so the customer sees where their request stands.
  const hasHistory = submittedRequests.length > 0
  const hasForm = (showRequestForm || preview) && requestable.length > 0
  const activeTab = (hasHistory && hasForm) ? crTab : (hasHistory ? 'history' : 'form')

  // Accept & pay: the plans the reseller enabled + the current paid/upcoming rows.
  const pp = data.paymentPlan || {}
  const payments = data.payments || []
  const planMeta = {
    full: q.language === 'fr' ? 'Payer en totalité' : 'Pay in full',
    deposit: q.language === 'fr' ? 'Dépôt + solde' : 'Deposit + balance',
    installments: q.language === 'fr' ? 'Versements' : 'Installments',
  }
  const allowedPlans = ['full', 'deposit', 'installments'].filter(k => pp.allowed && pp.allowed[k] && pp.plans && pp.plans[k])
  const selPlan = (acPlan && allowedPlans.includes(acPlan)) ? acPlan : allowedPlans[0]
  const accepted = ['accepted_paid', 'accepted_partial'].includes(q.status)
  const canPay = !!q.canAccept && !expired && allowedPlans.length > 0
  const numGolfers = Number(q.numGolfers) || 0
  // Offer split-pay when there's a group and we're not in the reseller preview.
  const canSplit = canPay && numGolfers >= 2 && !preview
  const payLabel = (p) => p.bucket === 'deposit' ? (fr ? 'Dépôt' : 'Deposit') : p.bucket === 'full' ? (fr ? 'Paiement' : 'Payment') : (p.installmentNo ? `${fr ? 'Versement' : 'Installment'} ${p.installmentNo}` : (fr ? 'Solde' : 'Balance'))
  const fr = q.language === 'fr'
  const planSummary = (k, pl) => {
    if (k === 'full') return fr ? "Un seul paiement aujourd'hui" : 'One payment today'
    if (k === 'deposit') return fr
      ? `${money(pl.firstCharge, cur)} aujourd'hui, ${money(pl.balance, cur)} d'ici le ${fmtFullDate(pl.balanceDueDate, 'fr')}`
      : `${money(pl.firstCharge, cur)} today, ${money(pl.balance, cur)} by ${fmtFullDate(pl.balanceDueDate)}`
    return fr ? `${pl.count} versements, à partir d'aujourd'hui` : `${pl.count} payments, starting today`
  }

  // Group the itinerary by calendar date. Golf lands on its play date; a hotel
  // is shown on EVERY night it covers (check-in → overnight → check-out) so the
  // stay reads as continuous instead of a single blip on the check-in day.
  const byDate = {}
  const ensure = (k) => (byDate[k] = byDate[k] || { rounds: [], hotelEvents: [] })
  rounds.forEach(r => { const k = ymd(r.playDate); if (k) ensure(k).rounds.push(r) })
  hotels.forEach(h => {
    const ci = ymd(h.checkInDate), co = ymd(h.checkOutDate)
    if (!ci) return
    const nights = Math.max(1, Number(h.nights) || (co ? daysBetweenY(ci, co) : 1) || 1)
    for (let n = 0; n < nights; n++) ensure(addDaysY(ci, n)).hotelEvents.push({ hotel: h, role: n === 0 ? 'checkin' : 'night', nights })
    if (co) ensure(co).hotelEvents.push({ hotel: h, role: 'checkout' })
  })
  const dateKeys = Object.keys(byDate).sort()

  // Multi-stop legs (display-only): the server groups located items into legs
  // with a label + date span. Show a leg header above the first day of each leg
  // when the trip spans >1 location. Single-location trips are unchanged.
  const legs = Array.isArray(data.legs) ? data.legs : []
  const multiLeg = legs.length > 1
  const legForDate = (dk) => {
    for (const lg of legs) {
      const s = lg.startDate ? ymd(lg.startDate) : null
      const e = lg.endDate ? ymd(lg.endDate) : s
      if (s && dk >= s && dk <= (e || s)) return lg
    }
    return null
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      {preview && (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '10px 14px', background: '#fef3c7', borderBottom: '1px solid #fde68a', color: '#92400e', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
          👁 {fr ? 'Aperçu — voici ce que le client verra. Rien n\'a été envoyé.' : 'Preview — this is what the customer will see. Nothing has been sent.'}
        </div>
      )}
      {/* Full-width branded header band (green spans the whole top) */}
      <div style={{ background: accent, padding: '22px 16px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {brand?.logoUrl
            ? <div style={{ flexShrink: 0, background: '#fff', borderRadius: 10, padding: '8px 12px' }}><img src={brand.logoUrl} alt={brand.name || ''} style={{ maxHeight: 60, maxWidth: 150, objectFit: 'contain', display: 'block' }} /></div>
            : <div style={{ flexShrink: 0, fontSize: 18, fontWeight: 800, color: onAccent }}>{brand?.name}</div>}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 24, color: onAccent, lineHeight: 1.2 }}>{q.name || (fr ? 'Votre voyage de golf' : 'Your Golf Trip')}</h1>
            <div style={{ display: 'inline-block', marginTop: 8, padding: '3px 12px', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 999, fontSize: 14, fontWeight: 700, letterSpacing: 1, fontFamily: 'monospace', color: onAccent }}>{q.quoteNumber}</div>
          </div>
          {!preview && data.portal?.slug && (
            <a href={`${window.location.origin}/r/${data.portal.slug}/customer/login`} style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999, border: `1px solid ${onAccent === '#fff' ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.25)'}`, background: 'rgba(255,255,255,0.14)', color: onAccent, fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 15 }}>&#128100;</span>{q.language === 'fr' ? 'Mon compte' : 'Sign in to your account'}
            </a>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px' }}>

        {q.canAccept && (
          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12.5, color: '#92400e', lineHeight: 1.45 }}>
            {q.language === 'fr'
              ? "À noter : les prix et la disponibilité des départs ne sont définitifs qu'une fois le paiement effectué. Les heures peuvent se remplir ou changer de prix; votre total peut donc différer de ce devis. Vous verrez tout changement avant de payer."
              : "Please note: tee-time prices and availability aren't final until your payment is complete. Times can sell out or change price, so your total may differ from this quote. You'll see any changes before you pay."}
          </div>
        )}

        {expired && (
          <Card style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
            <div style={{ color: '#991b1b', fontWeight: 600 }}>{fr ? 'Ce devis est expiré.' : 'This quote has expired.'}</div>
            <div style={{ color: '#7f1d1d', fontSize: 13, marginTop: 4 }}>{fr
              ? `Les prix et la disponibilité ne sont plus garantis — veuillez contacter ${brand?.name || 'votre fournisseur de voyage'} pour un devis à jour.`
              : `Prices and availability are no longer guaranteed — please contact ${brand?.name || 'your travel provider'} for an updated quote.`}</div>
          </Card>
        )}

        {/* Personalized message from the planner */}
        {planner?.message && (
          <Card>
            {q.customerName && <p style={{ margin: '0 0 12px', fontSize: 15, color: '#0f172a' }}>{fr ? 'Bonjour' : 'Hello'} {q.customerName},</p>}
            <div className="hiw-preview" style={{ fontSize: 14, lineHeight: 1.55, color: '#334155' }} dangerouslySetInnerHTML={{ __html: mdToHtml(planner.message) }} />
            {planner.name && <p style={{ margin: '14px 0 0', fontSize: 14, color: '#0f172a' }}>{planner.name}{planner.title ? `, ${planner.title}` : ''}</p>}
          </Card>
        )}

        {/* Trip summary */}
        <Card>
          {!planner?.message && q.customerName && <p style={{ margin: '0 0 10px', fontSize: 15 }}>{fr ? 'Bonjour' : 'Hi'} {q.customerName},</p>}
          {!planner?.message && <p style={{ margin: '0 0 12px', color: '#475569', fontSize: 14 }}>{fr ? 'Voici votre devis de voyage de golf personnalisé. Consultez les détails ci-dessous.' : 'Here’s your custom golf trip quote. Take a look at the details below.'}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Meta label="Destination" value={q.destination ? q.destination.split(',').slice(0, 2).join(',') : '—'} />
            <Meta label="Dates" value={q.tripStartDate ? `${fmtDay(q.tripStartDate, q.language)} · ${q.numDays} ${fr ? 'jour' : 'day'}${q.numDays === 1 ? '' : 's'}` : '—'} />
            <Meta label={fr ? 'Golfeurs' : 'Golfers'} value={q.numGolfers || '—'} />
            {q.validUntil && <Meta label={fr ? "Valide jusqu'au" : 'Valid until'} value={fmtDay(q.validUntil, q.language)} />}
          </div>
          {(q.customerEmail || q.customerPhone) && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', flexWrap: 'wrap', gap: '6px 22px', fontSize: 13.5 }}>
              {q.customerEmail && (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{fr ? 'Courriel' : 'Email'}</span>
                  <a href={`mailto:${q.customerEmail}`} style={{ color: '#334155', fontWeight: 600, textDecoration: 'none', overflowWrap: 'anywhere' }}>{q.customerEmail}</a>
                </span>
              )}
              {q.customerPhone && (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{fr ? 'Téléphone' : 'Phone'}</span>
                  <a href={`tel:${q.customerPhone}`} style={{ color: '#334155', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>{displayPhone(q.customerPhone)}</a>
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Itinerary — grouped by date (golf on its play date, hotel on check-in) */}
        {dateKeys.length > 0 && (
          <Card>
            <SectionTitle color={accent}>{fr ? 'Itinéraire' : 'Itinerary'}</SectionTitle>
            {(() => { let prevLeg = null; return dateKeys.map(dk => {
              const lg = legForDate(dk)
              const legLabel = lg ? lg.label : null
              const showLeg = multiLeg && legLabel && legLabel !== prevLeg
              if (legLabel) prevLeg = legLabel
              return (
              <div key={dk} style={{ marginTop: 12 }}>
                {showLeg && (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '14px 0 6px', paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: accent }}>📍 {legLabel.split(',').slice(0, 2).join(',')}</span>
                    {lg.startDate && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>{fmtDay(lg.startDate, q.language)}{lg.endDate && ymd(lg.endDate) !== ymd(lg.startDate) ? ` → ${fmtDay(lg.endDate, q.language)}` : ''}</span>}
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, color: accent, marginBottom: 2 }}>{fmtLongDay(dk, q.language)}</div>
                {/* Accommodation ALWAYS renders above golf on the same day (Todd's
                    standing rule) — hotel events first, then rounds. */}
                {byDate[dk].hotelEvents.map((ev, i) => ev.role === 'checkin'
                  ? <HotelCard key={'h' + i} hotel={ev.hotel} nights={ev.nights} fr={fr} cur={cur} />
                  : <div key={'h' + i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0 4px 2px', fontSize: 12.5, color: '#94a3b8' }}>
                      <span style={{ opacity: 0.7 }}>🏨</span>
                      <span>{ev.hotel.hotelName || (fr ? 'Hôtel' : 'Hotel')} · {ev.role === 'checkout' ? (fr ? 'départ' : 'check-out') : (fr ? 'nuitée' : 'overnight')}</span>
                    </div>
                )}
                {byDate[dk].rounds.map((r, i) => <CourseCard key={'r' + i} round={r} fr={fr} />)}
              </div>
              )
            }) })()}
          </Card>
        )}

        {/* Custom line items */}
        {customs.length > 0 && (
          <Card>
            <SectionTitle color={accent}>{fr ? 'Inclus' : 'Included'}</SectionTitle>
            {customs.map((c, i) => <ItinRow key={i} kind="EXTRA" color="#64748b" title={c.label || (fr ? 'Article' : 'Item')} sub="" />)}
          </Card>
        )}

        {/* Price breakdown */}
        <Card>
          <SectionTitle color={accent}>{fr ? 'Prix' : 'Price'}</SectionTitle>
          <PriceRow label={fr ? 'Prix du forfait' : 'Package price'} value={money(T.itemsSubtotal, cur)} />
          {T.serviceFee > 0 && (
            <PriceRow
              label={<span>{q.serviceFeeLabel || T.serviceFeeLabel || (fr ? 'Services professionnels' : 'Professional Services')}
                <span style={{ fontSize: 11, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '1px 7px', marginLeft: 6, whiteSpace: 'nowrap' }}>{fr ? 'Non remboursable' : 'Non-refundable'}</span>
              </span>}
              value={money(T.serviceFee, cur)} />
          )}
          {/* Expedia BP6b/CP1: the tax line must read "Taxes and fees" (not "tax"). */}
          {T.tax > 0 && <PriceRow label={`${fr ? 'Taxes et frais' : 'Taxes and fees'} (${Math.round((T.taxRate || 0) * 1000) / 10}%)`} value={money(T.tax, cur)} />}
          <div style={{ borderTop: `2px solid ${accent}`, marginTop: 8, paddingTop: 8 }}>
            <PriceRow label="Total" value={money(T.total, cur)} bold color={accent} />
            {T.perGolfer != null && <PriceRow label={`${fr ? 'Par golfeur' : 'Per golfer'} (${T.numGolfers})`} value={money(T.perGolfer, cur)} muted />}
          </div>
          {/* Expedia-required taxes-and-fees disclosure text. */}
          {T.tax > 0 && (
            <p style={{ fontSize: 10.5, color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.45 }}>
              {fr
                ? "Les taxes sont des frais de récupération de taxes versés aux fournisseurs (p. ex. les hôtels); pour plus de détails, consultez nos conditions d'utilisation. Les frais de service sont conservés à titre de compensation pour le traitement de votre réservation et peuvent inclure des frais facturés par les fournisseurs."
                : 'The taxes are tax recovery charges paid to vendors (e.g. hotels); for details, please see our Terms of Use. Service fees are retained as compensation in servicing your booking and may include fees charged by vendors.'}
            </p>
          )}
        </Card>

        {/* Accept & pay — or the confirmed payment schedule once accepted */}
        {accepted ? (
          <Card style={{ borderColor: '#bbf7d0' }}>
            <SectionTitle color={accent}>{fr ? 'Voyage confirmé' : 'Trip confirmed'}</SectionTitle>
            <div style={{ fontSize: 14, color: '#166534', fontWeight: 700, marginBottom: 6 }}>{fr ? '✓ Accepté' : '✓ Accepted'}{q.acceptedAt ? ` ${fr ? 'le' : 'on'} ${fmtDay(q.acceptedAt, q.language)}` : ''}</div>
            {/* Confirmation summary (Expedia CP1): restate the total package price +
                taxes and fees on the confirmation itself. */}
            <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 8, lineHeight: 1.5 }}>
              {fr ? 'Prix du forfait' : 'Package price'}: {money(T.itemsSubtotal, cur)}
              {T.tax > 0 ? ` · ${fr ? 'Taxes et frais' : 'Taxes and fees'}: ${money(T.tax, cur)}` : ''}
              {' · '}<b style={{ color: '#0f172a' }}>{fr ? 'Total' : 'Total'}: {money(T.total, cur)} {cur}</b>
            </div>
            {payments.map((p, i) => {
              // A single-payer scheduled row the customer can pay by card now
              // (split rows carry a golferId and are paid on each golfer's page).
              const canPayThis = p.status === 'scheduled' && !p.golferId && !preview
              const open = payOpenId === p.paymentId
              return (
              <div key={i} style={{ padding: '9px 0', borderTop: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{payLabel(p)}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.status === 'paid' ? (p.paidAt ? `${fr ? 'Payé le' : 'Paid'} ${fmtDay(p.paidAt, q.language)}` : (fr ? 'Payé' : 'Paid')) : (p.dueDate ? `${fr ? 'Échéance le' : 'Due'} ${fmtDay(p.dueDate, q.language)}` : (fr ? 'À venir' : 'Upcoming'))}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <b style={{ fontSize: 14 }}>{money(p.amount, cur)}</b>
                    {canPayThis
                      ? <button type="button" onClick={() => { setPayOpenId(open ? null : p.paymentId); setPayErr(null) }}
                          style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, border: `1px solid ${accent}`, background: open ? '#fff' : accent, color: open ? accent : onAccent, cursor: 'pointer' }}>
                          {open ? (fr ? 'Fermer' : 'Close') : (fr ? 'Payer' : 'Pay now')}</button>
                      : <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: p.status === 'paid' ? '#166534' : '#92400e', background: p.status === 'paid' ? '#dcfce7' : '#fef3c7' }}>{p.status === 'paid' ? (fr ? 'Payé' : 'Paid') : (fr ? 'Planifié' : 'Scheduled')}</span>}
                  </div>
                </div>
                {open && (
                  <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <input value={payCard.number}
                        onChange={e => setPayCard(c => ({ ...c, number: e.target.value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim() }))}
                        inputMode="numeric" autoComplete="cc-number" placeholder={fr ? 'Numéro de carte' : 'Card number'}
                        style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={payCard.expiry}
                          onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); setPayCard(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }}
                          inputMode="numeric" autoComplete="cc-exp" placeholder={fr ? 'MM / AA' : 'MM / YY'}
                          style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        <input value={payCard.cvc}
                          onChange={e => setPayCard(c => ({ ...c, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                          inputMode="numeric" autoComplete="cc-csc" placeholder="CVC"
                          style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    {payErr && <div style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>{payErr}</div>}
                    <button type="button" onClick={() => payScheduled(p.paymentId)} disabled={payBusy}
                      style={{ marginTop: 10, width: '100%', padding: '11px 16px', background: accent, color: onAccent, border: 'none', borderRadius: 8, fontSize: 14.5, fontWeight: 700, cursor: payBusy ? 'default' : 'pointer' }}>
                      {payBusy ? (fr ? 'Traitement…' : 'Processing…') : `${fr ? 'Payer' : 'Pay'} ${money(p.amount, cur)}`}
                    </button>
                  </div>
                )}
              </div>
              )
            })}
          </Card>
        ) : (canPay || (preview && allowedPlans.length > 0)) && (
          <Card style={preview ? { opacity: 0.75 } : { border: `2px solid ${accent}` }}>
            <SectionTitle color={accent}>{fr ? 'Accepter et payer' : 'Accept & pay'}</SectionTitle>
            {canSplit && !acEscalated && !acConflict && (
              <div style={{ display: 'flex', marginBottom: 14, border: `1px solid ${accent}`, borderRadius: 10, overflow: 'hidden' }}>
                {[['together', fr ? 'Je paie tout' : "I'll pay"], ['split', fr ? 'Diviser entre golfeurs' : 'Split between golfers']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setAcMode(k)}
                    style={{ flex: 1, padding: '9px 10px', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', background: acMode === k ? accent : '#fff', color: acMode === k ? onAccent : '#475569' }}>{label}</button>
                ))}
              </div>
            )}
            {acMode === 'split' && canSplit ? (
              <SplitPayPanel token={token} allowedPlans={allowedPlans} pp={pp} planMeta={planMeta}
                planSummary={planSummary} numGolfers={numGolfers} total={T.total} cur={cur}
                accent={accent} onAccent={onAccent} fr={fr} customerName={q.customerName}
                initialSplit={data.split} initialLeadToken={data.leadToken}
                onBooked={async () => { try { const r = await tournamentApi.get(`/public/quote/${token}`); setData(r.data) } catch (_) { /* ignore */ } }} />
            ) : acEscalated ? (
              <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 13.5, color: '#9a3412', lineHeight: 1.5 }}>
                {fr
                  ? <>Nous sommes désolés — l'heure de départ pour <b>{acEscalated.courseName}</b> vient d'être réservée et aucune autre heure n'est disponible ce jour-là. <b>Aucun montant ne vous a été facturé.</b> {brand?.name || 'Votre fournisseur de voyage'} vous contactera pour trouver une solution de rechange.</>
                  : <>We're sorry — the tee time for <b>{acEscalated.courseName}</b> was just taken and no other times are available that day. <b>You were not charged.</b> {brand?.name || 'Your travel provider'} will reach out to arrange an alternative.</>}
              </div>
            ) : acConflict ? (
              <div>
                <div style={{ padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e', marginBottom: 10, lineHeight: 1.5 }}>
                  {fr
                    ? <>L'heure de départ de {fmtTee(acConflict.currentTeeTime)} à <b>{acConflict.courseName}</b> vient d'être réservée. Choisissez-en une autre pour continuer — aucun montant ne sera facturé avant la confirmation.</>
                    : <>The {fmtTee(acConflict.currentTeeTime)} tee time at <b>{acConflict.courseName}</b> was just taken. Pick another to continue — you won't be charged until it's confirmed.</>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {acConflict.alternates.map((o, i) => (
                    <button key={i} type="button" onClick={() => pickConflictAlternate(o, selPlan)} disabled={acBusy}
                      style={{ padding: '7px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: `1px solid ${accent}`, background: '#fff', color: '#0f172a', cursor: acBusy ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {fmtTee(o.teeTime)}
                      {o.delta != null && o.delta !== 0 && <span style={{ fontSize: 11, fontWeight: 700, color: o.delta > 0 ? '#b45309' : '#166534' }}>{o.delta > 0 ? '+' : '−'}{money(Math.abs(o.delta), cur)}</span>}
                    </button>
                  ))}
                </div>
                {acErr && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 13 }}>{acErr}</div>}
                {acBusy && <div style={{ marginTop: 8, fontSize: 12.5, color: '#64748b' }}>{fr ? 'Réservation de votre nouvelle heure…' : 'Securing your new time…'}</div>}
                <button type="button" onClick={() => { setAcConflict(null); setAcErr(null) }} style={{ marginTop: 10, background: 'none', border: 'none', color: '#64748b', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>{fr ? 'Annuler' : 'Cancel'}</button>
              </div>
            ) : (
              <>
                {allowedPlans.length > 1 && (
                  <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                    {allowedPlans.map(k => {
                      const pl = pp.plans[k]; const active = selPlan === k
                      return (
                        <button key={k} type="button" onClick={() => { setAcPlan(k); setAcErr(null) }} disabled={preview}
                          style={{ textAlign: 'left', border: `1.5px solid ${active ? accent : '#e2e8f0'}`, background: active ? '#f8fafc' : '#fff', borderRadius: 10, padding: '10px 12px', cursor: preview ? 'default' : 'pointer' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{planMeta[k]}</span>
                            <span style={{ fontSize: 13, color: '#475569' }}>{money(pl.firstCharge, cur)} {fr ? "aujourd'hui" : 'today'}</span>
                          </div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{planSummary(k, pl)}</div>
                        </button>
                      )
                    })}
                  </div>
                )}
                {selPlan && pp.plans[selPlan] && (
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                    {pp.plans[selPlan].schedule.map((s, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
                        <span style={{ color: '#475569' }}>{i === 0 ? (fr ? "Aujourd'hui" : 'Today') : (s.when && s.when !== 'now' ? fmtFullDate(s.when, q.language) : (fr ? 'Plus tard' : 'Later'))}</span>
                        <b>{money(s.amount, cur)}</b>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'grid', gap: 8 }}>
                  {/* Format as you type — same pattern as the embed checkout:
                      card # in 4-digit groups, expiry as MM / YY, CVC digits only. */}
                  <input value={acCard.number}
                    onChange={e => setAcCard(c => ({ ...c, number: e.target.value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim() }))}
                    disabled={preview} inputMode="numeric" autoComplete="cc-number" placeholder={fr ? 'Numéro de carte' : 'Card number'}
                    style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={acCard.expiry}
                      onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); setAcCard(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }}
                      disabled={preview} inputMode="numeric" autoComplete="cc-exp" placeholder={fr ? 'MM / AA' : 'MM / YY'}
                      style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                    <input value={acCard.cvc}
                      onChange={e => setAcCard(c => ({ ...c, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      disabled={preview} inputMode="numeric" autoComplete="cc-csc" placeholder="CVC"
                      style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                </div>
                {selPlan && selPlan !== 'full' && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                    <button type="button" role="switch" aria-checked={acAutoPay} disabled={preview}
                      onClick={() => !preview && setAcAutoPay(v => !v)}
                      style={{ flexShrink: 0, marginTop: 1, width: 38, height: 22, borderRadius: 999, border: 'none', padding: 0, position: 'relative', cursor: preview ? 'default' : 'pointer', background: acAutoPay ? accent : '#cbd5e1', transition: 'background 0.15s' }}>
                      <span style={{ position: 'absolute', top: 2, left: acAutoPay ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
                    </button>
                    <span onClick={() => !preview && setAcAutoPay(v => !v)} style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.4, cursor: preview ? 'default' : 'pointer' }}>{fr
                      ? "Enregistrer ma carte et débiter automatiquement les paiements à venir à leur échéance."
                      : 'Save my card and automatically charge the future payments on their due dates.'}</span>
                  </div>
                )}
                {acNotice && <div style={{ marginTop: 10, padding: '8px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12.5, color: '#1e40af' }}>{acNotice}</div>}
                {acErr && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 13 }}>{acErr}</div>}
                <button type="button" onClick={() => acceptAndPay(selPlan)} disabled={preview || acBusy}
                  style={{ marginTop: 12, width: '100%', padding: '12px 16px', background: preview ? '#cbd5e1' : accent, color: preview ? '#64748b' : onAccent, border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: preview ? 'default' : 'pointer' }}>
                  {preview
                    ? (fr ? 'Aperçu — les clients acceptent et paient ici' : 'Preview — customers accept & pay here')
                    : acBusy ? (fr ? 'Traitement…' : 'Processing…')
                    : (fr ? `Payer ${money(pp.plans[selPlan] ? pp.plans[selPlan].firstCharge : 0, cur)} et confirmer` : `Pay ${money(pp.plans[selPlan] ? pp.plans[selPlan].firstCharge : 0, cur)} & confirm`)}
                </button>
                {acBusy && (
                  <div style={{ marginTop: 12, padding: '12px 14px', background: '#f0fdf4', border: `1px solid ${accent}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <style>{`@keyframes ig-spin { to { transform: rotate(360deg); } }`}</style>
                    <span style={{ flexShrink: 0, width: 20, height: 20, border: `2.5px solid ${accent}`, borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'ig-spin 0.8s linear infinite' }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {(fr ? AC_STEPS_FR : AC_STEPS_EN)[Math.min(acStep, (fr ? AC_STEPS_FR : AC_STEPS_EN).length - 1)]}
                      </div>
                      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                        {fr
                          ? 'Cela peut prendre jusqu’à une minute. Merci de ne pas fermer ni actualiser cette page.'
                          : 'This can take up to a minute. Please don’t close or refresh this page.'}
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{fr ? 'Sécurisé par InteGolf · carte traitée via Moneris.' : 'Secured by InteGolf · card processed through Moneris.'}</div>
              </>
            )}
          </Card>
        )}

        {/* Change requests — history + request form, tabbed when both are present */}
        {(hasHistory || hasForm) && (
          <Card>
            {hasHistory && hasForm ? (
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[['history', fr ? 'Vos demandes de modification' : 'Your change requests'], ['form', fr ? 'Demander des modifications' : 'Request changes']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setCrTab(k)}
                    style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: `1px solid ${activeTab === k ? accent : '#e2e8f0'}`, background: activeTab === k ? accent : '#fff', color: activeTab === k ? onAccent : '#475569' }}>
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <SectionTitle color={accent}>{hasHistory ? (fr ? 'Vos demandes de modification' : 'Your change requests') : (fr ? 'Demander des modifications' : 'Request changes')}</SectionTitle>
            )}

            {/* History: already-submitted requests + any reseller replies */}
            {activeTab === 'history' && submittedRequests.map(m => (
              <div key={m.id} style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
                {m.authorType === 'reseller' ? (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: accent, marginBottom: 2 }}>{fr ? `${brand?.name || 'Votre fournisseur de voyage'} a répondu` : `${brand?.name || 'Your travel provider'} replied`}</div>
                    <div style={{ fontSize: 13.5, color: '#334155', whiteSpace: 'pre-wrap' }}>{m.body}</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: m.requestKind === 'remove' ? '#dc2626' : m.requestKind === 'comment' ? '#64748b' : '#d97706', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', marginTop: 1 }}>
                      {m.requestKind === 'remove' ? (fr ? 'RETIRER' : 'REMOVE') : m.requestKind === 'comment' ? 'NOTE' : (fr ? 'MODIFIER' : 'CHANGE')}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{m.itemLabel || (fr ? 'Commentaire général' : 'General comment')}</div>
                      {m.body && <div style={{ fontSize: 13, color: '#475569', marginTop: 1, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap', color: m.resolved ? '#166534' : '#92400e', background: m.resolved ? '#dcfce7' : '#fef3c7' }}>
                      {m.resolved ? (fr ? '✓ Traité' : '✓ Handled') : (fr ? 'En attente' : 'Pending')}
                    </span>
                  </div>
                )}
              </div>
            ))}

            {/* Request form (live quotes only; disabled in preview) */}
            {activeTab === 'form' && (
              <div style={preview ? { opacity: 0.75 } : undefined}>
                <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#475569' }}>
                  {fr
                    ? `Besoin d'un ajustement ? Indiquez ce que vous souhaitez modifier ou retirer, ajoutez une note, et ${brand?.name || 'votre fournisseur de voyage'} vous enverra un devis à jour.`
                    : `Need something adjusted? Mark what you'd like to change or remove, add a note, and ${brand?.name || 'your travel provider'} will send you an updated quote.`}
                </p>
                {requestable.map(it => {
                  const sel = crSel[it.id] || {}
                  return (
                    <div key={it.id} style={{ padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: it.color, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', marginTop: 2 }}>{it.kind}</span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{it.title}</div>
                          {it.sub && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{it.sub}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <RequestToggle active={sel.mode === 'change'} onClick={() => setItemMode(it.id, 'change')} disabled={preview} accent={accent} label={fr ? 'Modifier' : 'Change'} />
                          <RequestToggle active={sel.mode === 'remove'} onClick={() => setItemMode(it.id, 'remove')} disabled={preview} accent="#dc2626" label={fr ? 'Retirer' : 'Remove'} />
                        </div>
                      </div>
                      {sel.mode === 'change' && (it.kind === 'GOLF'
                        ? <>
                            <TeeTimePicker token={token} itemId={it.id} cur={cur} accent={accent} fr={fr} onApplied={(d) => { setData(d); setItemMode(it.id, null) }} />
                            <textarea value={sel.note || ''} onChange={e => setItemNote(it.id, e.target.value)} disabled={preview}
                              placeholder={fr ? 'Ou décrivez une autre modification — p. ex. un autre parcours, une autre journée…' : 'Or describe a different change — e.g. a different course, another day…'}
                              style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', minHeight: 52, boxSizing: 'border-box' }} />
                          </>
                        : <textarea value={sel.note || ''} onChange={e => setItemNote(it.id, e.target.value)} disabled={preview}
                            placeholder={fr ? 'Que souhaitez-vous modifier ? (p. ex. une suite, une meilleure chambre)' : 'What would you like changed? (e.g. a suite instead, a better room)'}
                            style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', minHeight: 60, boxSizing: 'border-box' }} />
                      )}
                    </div>
                  )
                })}
                <div style={{ marginTop: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3 }}>{fr ? 'Autre chose ?' : 'Anything else?'}</label>
                  <textarea value={crComment} onChange={e => { setCrComment(e.target.value); setCrDone(false) }} disabled={preview}
                    placeholder={fr ? 'Ajoutez un commentaire — p. ex. ajouter une ronde au jour 2, changer les dates, questions sur le voyage…' : 'Add a comment — e.g. add a round on day 2, change the dates, questions about the trip…'}
                    style={{ width: '100%', marginTop: 6, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', minHeight: 70, boxSizing: 'border-box' }} />
                </div>
                {crErr && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 13 }}>{crErr}</div>}
                {crDone && <div style={{ marginTop: 10, color: '#166534', fontSize: 13, fontWeight: 600 }}>{fr ? `Envoyé ! ${brand?.name || 'Votre fournisseur de voyage'} examinera vos demandes et vous répondra.` : `Sent! ${brand?.name || 'Your travel provider'} will review your requests and follow up.`}</div>}
                <button type="button" onClick={submitChangeRequest} disabled={preview || crBusy}
                  style={{ marginTop: 14, width: '100%', padding: '11px 16px', background: preview ? '#cbd5e1' : accent, color: preview ? '#64748b' : onAccent, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: preview ? 'default' : 'pointer' }}>
                  {preview ? (fr ? 'Aperçu — les clients demandent des modifications ici' : 'Preview — customers request changes here') : crBusy ? (fr ? 'Envoi…' : 'Sending…') : (fr ? 'Envoyer mes demandes' : 'Send my requests')}
                </button>
              </div>
            )}
          </Card>
        )}

        {/* Planner card */}
        {planner && (planner.name || planner.email) && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {planner.photoUrl && <img src={planner.photoUrl} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />}
              <div style={{ minWidth: 0 }}>
                {planner.name && <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{planner.name}</div>}
                {planner.title && <div style={{ fontSize: 13, color: '#64748b' }}>{planner.title}</div>}
                {planner.email && <a href={`mailto:${planner.email}`} style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none' }}>{planner.email}</a>}
                {planner.phone && <div style={{ fontSize: 13, color: '#64748b' }}>{planner.phone}</div>}
              </div>
            </div>
          </Card>
        )}

        {/* Branded footer: business name in brand colour, planner contact + reply note */}
        <div style={{ marginTop: 18, borderTop: `2px solid ${accent}`, paddingTop: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: accent, marginBottom: 6 }}>{brand?.name || 'Your travel provider'}</div>
          {(planner?.email || planner?.phone) && (
            <div style={{ fontSize: 13, color: '#475569', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
              {planner?.email && <a href={`mailto:${planner.email}`} style={{ color: accent, textDecoration: 'none', fontWeight: 600 }}>{planner.email}</a>}
              {planner?.phone && <span>{planner.phone}</span>}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            {fr
              ? `Des questions sur ce devis ? Répondez au courriel de ${brand?.name || 'votre fournisseur de voyage'}.`
              : `Questions about this quote? Reply to the email from ${brand?.name || 'your travel provider'}.`}
          </div>
        </div>
      </div>
    </div>
  )
}

function Centered({ children }) {
  return <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>{children}</div>
}
function Card({ children, style }) {
  return <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12, ...style }}>{children}</div>
}
function SectionTitle({ children, color }) {
  return <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color, marginBottom: 8, textTransform: 'uppercase' }}>{children}</div>
}
function Meta({ label, value }) {
  return <div><div style={{ fontSize: 11, color: '#94a3b8' }}>{label}</div><div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{value}</div></div>
}
// One itinerary line with a coloured GOLF / HOTEL tag (no prices — Expedia rule).
// A scenic imageUrl (admin-set course photo) shows a large cover thumbnail;
// otherwise an optional logoUrl shows a small course logo; otherwise just the tag.
function ItinRow({ kind, title, sub, color, logoUrl, imageUrl }) {
  const lead = imageUrl
    ? <img src={imageUrl} alt="" style={{ width: 104, height: 78, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
    : logoUrl
      ? <img src={logoUrl} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6, background: '#fff', border: '1px solid #f1f5f9', flexShrink: 0, marginTop: 1 }} />
      : <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: color, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', marginTop: 1 }}>{kind}</span>
  const showTagInline = imageUrl || logoUrl
  return (
    <div style={{ display: 'flex', gap: imageUrl ? 12 : 10, padding: imageUrl ? '10px 0' : '8px 0', borderTop: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
      {lead}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
          {showTagInline && <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: color, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>{kind}</span>}
          <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{title}</span>
        </div>
        {sub && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{sub}</div>}
      </div>
    </div>
  )
}

// Image-led golf row for the itinerary — mirrors the hotel block with labeled
// lines (address, distance from the hotel, tee/requested time, golfers, phone).
// When the tee time hasn't been booked yet it's clearly a "Requested time", not
// a confirmed one; a booked round shows the exact time + a confirmation line.
function CourseCard({ round: r, fr }) {
  const booked = !!r.bookedTeeTime
  // A leading "~" marks an approximate/requested band; the "Requested time" label
  // already conveys that, so strip it for a clean read.
  const timeRaw = booked ? r.bookedTeeTime : String(r.teeTime || '').replace(/^~/, '')
  const timeStr = fmtTee(timeRaw)
  // Only a RANGE (two times, e.g. 9:00–10:00 AM) is a requested window. A single
  // exact time has been pinned/confirmed, so it's the Tee time — whether or not
  // it was booked through an integration.
  const isRange = (String(timeRaw).match(/\d{1,2}:\d{2}/g) || []).length >= 2
  const golfersTxt = r.golfers ? `${r.golfers} ${fr ? 'golfeur' : 'golfer'}${r.golfers === 1 ? '' : 's'}` : null
  const distTxt = r.hotelDistanceKm != null
    ? `${r.hotelDistanceKm} km${r.hotelDistanceFrom ? ` · ${r.hotelDistanceFrom}` : ''}`
    : null
  const Row = ({ label, children }) => children ? (
    <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}><span style={{ color: '#94a3b8' }}>{label}: </span>{children}</div>
  ) : null
  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
      {r.courseImageUrl
        ? <img src={r.courseImageUrl} alt={r.courseName || ''} style={{ width: 104, height: 78, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
        : r.courseLogoUrl
          ? <img src={r.courseLogoUrl} alt={r.courseName || ''} style={{ width: 104, height: 78, objectFit: 'contain', borderRadius: 8, background: '#fff', border: '1px solid #f1f5f9', flexShrink: 0 }} />
          : <div style={{ width: 104, height: 78, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 24 }}>⛳</div>}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#16a34a', padding: '3px 8px', borderRadius: 6 }}>{fr ? 'GOLF' : 'GOLF'}</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>{r.courseName || 'Golf'}</span>
        </div>
        <div style={{ marginTop: 5 }}>
          <Row label={fr ? 'Adresse' : 'Address'}>{r.courseAddress}</Row>
          <Row label={fr ? "Distance de l'hôtel" : 'From hotel'}>{distTxt}</Row>
          <Row label={isRange ? (fr ? 'Heure demandée' : 'Requested time') : (fr ? 'Heure de départ' : 'Tee time')}>{timeStr}</Row>
          <Row label={fr ? 'Golfeurs' : 'Golfers'}>{golfersTxt}</Row>
          <Row label={fr ? 'Téléphone' : 'Phone'}>{r.coursePhone ? displayPhone(r.coursePhone) : null}</Row>
        </div>
        {r.golfConfirmation && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: '#166534' }}>
            {fr ? 'Confirmation du départ' : 'Tee time confirmation'}: <b>{r.golfConfirmation}</b>
          </div>
        )}
      </div>
    </div>
  )
}

// Image-led hotel row for the itinerary (photo + stars + guest rating + a short
// blurb) — matches the richer look partners expect from their portal.
function HotelCard({ hotel, nights, fr, cur }) {
  const stars = hotel.hotelStars ? Math.round(hotel.hotelStars) : 0
  const fmtD = (d) => { if (!d) return null; const x = new Date(d); return isNaN(x) ? null : x.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) }
  // Hotel times arrive in two shapes: Expedia content is already 12-hour
  // ("4:00 PM"), RateHawk is 24-hour ("14:00"). Pass a value that already has an
  // AM/PM suffix through unchanged (normalized) — only convert bare 24-hour times,
  // so "4:00 PM" doesn't get mis-read as "4:00 AM".
  const fmtT = (t) => {
    if (!t) return null
    const s = String(t).trim()
    const ampm = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/)
    if (ampm) return `${+ampm[1]}:${ampm[2]} ${ampm[3].toUpperCase()}`
    const m = s.match(/^(\d{1,2}):(\d{2})/)
    if (!m) return s
    let h = +m[1]; const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${m[2]} ${ap}`
  }
  const ci = fmtD(hotel.checkInDate), co = fmtD(hotel.checkOutDate)
  const cit = fmtT(hotel.checkInTime), cot = fmtT(hotel.checkOutTime)
  const roomLine = [hotel.roomType, hotel.rooms ? `${hotel.rooms} ${fr ? 'chambre' : 'room'}${hotel.rooms === 1 ? '' : 's'}` : null, `${nights} ${fr ? 'nuit' : 'night'}${nights === 1 ? '' : 's'}`].filter(Boolean).join(' · ')
  const guestsTxt = hotel.guests ? `${hotel.guests} ${fr ? (hotel.guests === 1 ? 'personne' : 'personnes') : (hotel.guests === 1 ? 'guest' : 'guests')}` : null
  const Row = ({ label, children }) => children ? (
    <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}><span style={{ color: '#94a3b8' }}>{label}: </span>{children}</div>
  ) : null

  // Expedia-cert disclosures (from the snapshotted rate + property content).
  // Cancel-schedule dates carry the property's UTC offset — format the wall-clock
  // components directly so they read in the PROPERTY's local time, not the viewer's.
  const fmtLocalDT = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
    if (!m) return ''
    const dt = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5])
    const date = dt.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { day: '2-digit', month: 'short', year: 'numeric' })
    const time = dt.toLocaleTimeString(fr ? 'fr-CA' : 'en-US', { hour: 'numeric', minute: '2-digit', hour12: !fr })
    return `${date}, ${time}`
  }
  const cx = hotel.cancellation || null
  const cxBullets = []
  if (cx && cx.refundable && cx.freeCancelBefore) {
    cxBullets.push(fr ? `Annulation gratuite jusqu'au ${fmtLocalDT(cx.freeCancelBefore)}.` : `Free cancellation until ${fmtLocalDT(cx.freeCancelBefore)}.`)
    if (cx.penaltyNights && cx.nonRefundableAfter) {
      const n = cx.penaltyNights
      cxBullets.push(fr
        ? `Après le ${fmtLocalDT(cx.freeCancelBefore)}, une annulation entraîne une pénalité de ${n} nuit${n > 1 ? 's' : ''} plus taxes et frais jusqu'au ${fmtLocalDT(cx.nonRefundableAfter)}.`
        : `After ${fmtLocalDT(cx.freeCancelBefore)}, cancellations will result in a ${n} night penalty plus taxes and fees until ${fmtLocalDT(cx.nonRefundableAfter)}.`)
    }
    if (cx.nonRefundableAfter) {
      cxBullets.push(fr
        ? `Après le ${fmtLocalDT(cx.nonRefundableAfter)}, la réservation devient entièrement non remboursable.`
        : `The end time for the cancellation window is ${fmtLocalDT(cx.nonRefundableAfter)}, at which time the booking becomes fully non-refundable.`)
    }
    cxBullets.push(fr
      ? `Si vous ne vous présentez pas, ou si vous annulez ou modifiez cette réservation après l'arrivée, des frais de pénalité peuvent s'appliquer à la discrétion de l'établissement (jusqu'à 100 % de la valeur de la réservation).`
      : `If you fail to check in, or if you cancel or change this reservation after check-in, you may incur penalty charges at the property's discretion (up to 100% of the booking value).`)
  } else if (cx && cx.refundable === false) {
    cxBullets.push(fr ? 'Ce tarif est non remboursable.' : 'This rate is non-refundable.')
  }
  // Charges collected AT THE PROPERTY (resort fee + mandatory tax).
  const fdp = hotel.feesDueAtProperty || null
  const dpCur = (fdp && fdp.currency) || hotel.resortFeeCurrency || cur
  const resortAmt = fdp && fdp.resortFee != null ? fdp.resortFee : (hotel.resortFee != null ? hotel.resortFee : null)
  const dueAtProperty = []
  if (resortAmt != null && resortAmt > 0) dueAtProperty.push(`${fr ? 'Frais de villégiature' : 'Resort fee'} ${money(resortAmt, dpCur)}`)
  if (fdp && fdp.mandatoryTax != null && fdp.mandatoryTax > 0) dueAtProperty.push(`${fr ? 'Taxe obligatoire' : 'Mandatory tax'} ${money(fdp.mandatoryTax, dpCur)}`)
  const feesHtml = hotel.hotelFees ? [hotel.hotelFees.mandatory, hotel.hotelFees.optional].filter(Boolean).join('') : null
  const policiesHtml = hotel.hotelPolicies ? (hotel.hotelPolicies.know_before_you_go || hotel.hotelPolicies.knowBeforeYouGo || null) : null

  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderTop: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
      {hotel.hotelImageUrl
        ? <img src={hotel.hotelImageUrl} alt={hotel.hotelName || ''} style={{ width: 104, height: 78, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
        : <div style={{ width: 104, height: 78, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 24 }}>🏨</div>}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#7c3aed', padding: '3px 8px', borderRadius: 6 }}>{fr ? 'HÔTEL' : 'HOTEL'}</span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>{hotel.hotelName || (fr ? 'Hôtel' : 'Hotel')}</span>
          {stars > 0 && <span style={{ color: '#f59e0b', fontSize: 12, letterSpacing: 1 }}>{'★'.repeat(stars)}</span>}
          {/* Expedia requires PACKAGE rates to carry a unique label; a hotel_only
              (standalone) rate must NOT be shown as a package rate, and a non-Expedia
              supplier (RateHawk/Nuitée/net_rate) is never a package rate at all. NULL
              env on an Expedia hotel = legacy rate = package (back-compat). */}
          {String(hotel.source || '').toLowerCase() === 'expedia' && hotel.salesEnvironment !== 'hotel_only' && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#6d28d9', background: '#f3e8ff', border: '1px solid #e9d5ff', padding: '2px 7px', borderRadius: 6 }}>{fr ? 'Tarif forfait' : 'Package rate'}</span>
          )}
        </div>
        {hotel.hotelGuestRating != null && (
          <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
            <span style={{ background: '#065f46', color: '#fff', fontWeight: 700, padding: '2px 6px', borderRadius: 5 }}>{Number(hotel.hotelGuestRating).toFixed(1)}</span>
            <span style={{ color: '#94a3b8' }}>{hotel.hotelReviewCount ? `${hotel.hotelReviewCount} ${fr ? 'avis' : 'reviews'}` : (fr ? 'note des clients' : 'guest rating')}</span>
          </div>
        )}
        <div style={{ marginTop: 5 }}>
          <Row label={fr ? 'Adresse' : 'Address'}>{hotel.hotelAddress}</Row>
          <Row label={fr ? 'Clients' : 'Guests'}>{guestsTxt}</Row>
          <Row label={fr ? 'Chambre' : 'Room'}>{roomLine}</Row>
          <Row label={fr ? 'Arrivée' : 'Check-in'}>{ci ? `${ci}${cit ? ` · ${fr ? 'après' : 'after'} ${cit}` : ''}` : null}</Row>
          <Row label={fr ? 'Départ' : 'Check-out'}>{co ? `${co}${cot ? ` · ${fr ? 'avant' : 'before'} ${cot}` : ''}` : null}</Row>
          {/* Expedia BP4/ER5: charges collected AT THE PROPERTY, listed separately. */}
          {dueAtProperty.length > 0 && (
            <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2 }}>
              <span style={{ color: '#94a3b8' }}>{fr ? "À payer à l'hôtel" : 'Due at the property'}: </span>{dueAtProperty.join(' · ')}
            </div>
          )}
        </div>
        {hotel.hotelDescription && <div style={{ marginTop: 5, fontSize: 12.5, color: '#475569', lineHeight: 1.45 }}>{hotel.hotelDescription}</div>}
        {hotel.hotelConfirmation && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: '#6d28d9' }}>
            {fr ? 'Confirmation de l\'hôtel' : 'Hotel confirmation'}: <b>{hotel.hotelConfirmation}</b>
          </div>
        )}
        {/* Expedia-required disclosures: cancellation policy (property-local time),
            special check-in instructions, fees, and policies. Expandable to keep
            the card compact; cancellation is open by default. */}
        {cxBullets.length > 0 && (
          <Disclosure label={fr ? "Politique d'annulation" : 'Cancellation policy'} open>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.5 }}>
              {cxBullets.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
            </ul>
          </Disclosure>
        )}
        {hotel.checkInInstructions && (
          <Disclosure label={fr ? "Instructions d'arrivée" : 'Check-in instructions'}>
            <div style={{ lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: hotel.checkInInstructions }} />
          </Disclosure>
        )}
        {feesHtml && (
          <Disclosure label={fr ? 'Frais' : 'Fees'}>
            <div style={{ lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: feesHtml }} />
          </Disclosure>
        )}
        {policiesHtml && (
          <Disclosure label={fr ? 'Politiques' : 'Policies'}>
            <div style={{ lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: policiesHtml }} />
          </Disclosure>
        )}
      </div>
    </div>
  )
}

// Compact expandable disclosure (native <details>) for the hotel card's
// Expedia-required sections (cancellation / fees / policies / check-in).
function Disclosure({ label, children, open = false }) {
  return (
    <details open={open} style={{ marginTop: 6 }}>
      <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#0369a1', listStyle: 'none' }}>ⓘ {label}</summary>
      <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, paddingLeft: 2 }}>{children}</div>
    </details>
  )
}
// Live tee-time swap: fetches available times for a round (current one centered),
// shows each with its price delta, and applies the pick on the spot (updates the
// whole quote via onApplied). Live prices win — the quote isn't a guarantee.
function TeeTimePicker({ token, itemId, cur, accent, onApplied, fr }) {
  const [state, setState] = useState({ loading: true, err: null, data: null })
  const [applying, setApplying] = useState(null)
  useEffect(() => {
    let live = true
    tournamentApi.get(`/public/quote/${token}/round/${itemId}/alternate-times`)
      .then(r => { if (live) setState({ loading: false, err: r.data.ok === false ? (r.data.message || (fr ? 'Aucune heure en direct pour le moment.' : 'No live times right now.')) : null, data: r.data }) })
      .catch(e => { if (live) setState({ loading: false, err: e?.response?.data?.message || e.message, data: null }) })
    return () => { live = false }
  }, [token, itemId])

  async function apply(opt) {
    setApplying(opt.ref || opt.teeTime); setState(s => ({ ...s, err: null }))
    try {
      const r = await tournamentApi.post(`/public/quote/${token}/round/${itemId}/change-time`, { teeTime: opt.teeTime, ref: opt.ref })
      onApplied(r.data)
    } catch (e) {
      setState(s => ({ ...s, err: e?.response?.data?.message || e.message })); setApplying(null)
    }
  }

  if (state.loading) return <div style={{ padding: '8px 2px', fontSize: 12.5, color: '#64748b' }}>{fr ? 'Chargement des heures disponibles…' : 'Loading available times…'}</div>
  const opts = state.data?.options || []
  if (!opts.length) return <div style={{ padding: '8px 2px', fontSize: 12.5, color: '#991b1b' }}>{state.err || (fr ? 'Aucune heure de départ en direct pour le moment.' : 'No live tee times available right now.')}</div>
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
        {fr ? 'Choisissez une nouvelle heure' : 'Pick a new time'}{state.data?.current?.available === false ? (fr ? ' — votre heure actuelle n\'est plus disponible' : ' — your current time is no longer available') : ''} :
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {opts.map((o, i) => {
          if (o.isCurrent) {
            return (
              <span key={i} title={fr ? 'Votre heure actuelle' : 'Your current time'} style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, border: `1.5px solid ${accent}`, background: '#fff', color: accent, whiteSpace: 'nowrap' }}>
                {fmtTee(o.teeTime)} ✓
              </span>
            )
          }
          const busy = applying === (o.ref || o.teeTime)
          const dnz = o.delta != null && o.delta !== 0
          return (
            <button key={i} type="button" onClick={() => apply(o)} disabled={!!applying}
              title={o.delta != null && o.delta !== 0 ? `${money(o.totalPrice, cur)} total` : undefined}
              style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', cursor: applying ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap' }}>
              {fmtTee(o.teeTime)}
              {dnz && <span style={{ fontSize: 11, fontWeight: 700, color: o.delta > 0 ? '#b45309' : '#166534' }}>{o.delta > 0 ? '+' : '−'}{money(Math.abs(o.delta), cur)}</span>}
            </button>
          )
        })}
      </div>
      {state.err && <div style={{ marginTop: 6, fontSize: 12.5, color: '#991b1b' }}>{state.err}</div>}
    </div>
  )
}

// Small pill button for a per-item change/remove selection.
function RequestToggle({ active, onClick, disabled, accent, label }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{
        padding: '5px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${active ? accent : '#e2e8f0'}`,
        background: active ? accent : '#fff',
        color: active ? contrastText(accent) : '#475569',
      }}>
      {label}
    </button>
  )
}
function PriceRow({ label, value, bold, muted, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: bold ? 16 : 13.5, fontWeight: bold ? 800 : 400, color: color || (muted ? '#64748b' : (bold ? '#0f172a' : '#475569')) }}>
      <span>{label}</span><span>{value}</span>
    </div>
  )
}

// n even 2-dp shares of `total`, last absorbing the rounding remainder.
function evenSplit(total, n) {
  const per = Math.round((Number(total) / n) * 100) / 100
  const arr = Array.from({ length: n }, () => per)
  arr[n - 1] = Math.round((Number(total) - per * (n - 1)) * 100) / 100
  return arr
}

// Split-pay panel (lead flow): build the roster + shares, start the split, pay
// the lead's own share, then a live waiting screen that polls collection status,
// can extend the hold, and lets the lead cover any stragglers. Each golfer pays
// their own share via the emailed "pay your share" link; the trip books only
// once every share is authorized. Prices (Expedia rule) never shown — package
// total + per-golfer shares only.
function SplitPayPanel({ token, allowedPlans, pp, planMeta, planSummary, numGolfers, total, cur, accent, onAccent, fr, customerName, onBooked, initialSplit, initialLeadToken }) {
  // Resume an in-flight split: seed from the persisted state passed in so a
  // page refresh lands back on the waiting screen instead of a blank roster.
  const resuming = !!(initialLeadToken && initialSplit && ['collecting', 'booking'].includes(initialSplit.status))
  const [plan, setPlan] = useState((initialSplit && allowedPlans.includes(initialSplit.plan)) ? initialSplit.plan : allowedPlans[0])
  const [shareMode, setShareMode] = useState('even') // 'even' | 'custom'
  const [rows, setRows] = useState(() => {
    const shares = evenSplit(total, Math.max(2, numGolfers))
    return Array.from({ length: Math.max(2, numGolfers) }, (_, i) => ({ name: i === 0 ? (customerName || '') : '', email: '', share: shares[i], isLead: i === 0 }))
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [leadToken, setLeadToken] = useState(resuming ? initialLeadToken : null)
  const [status, setStatus] = useState(resuming ? initialSplit : null)      // the split payload
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '' })
  const [coverMode, setCoverMode] = useState(false)
  const [copiedId, setCopiedId] = useState(null)
  const [remindedId, setRemindedId] = useState(null)
  const [extendErr, setExtendErr] = useState(null)
  const [now, setNow] = useState(Date.now())
  // Flipped the instant the live countdown reaches zero, so the existing expired
  // block replaces the roster on the same second (`now` only ticks per-minute, so
  // on its own it would leave a gap after the clock disappears).
  const [cdExpired, setCdExpired] = useState(false)
  // `now` ticks once a minute as a fallback for the expired flip (the status poll
  // also refreshes); the SplitCountdown drives the exact-second flip via cdExpired.
  useEffect(() => {
    if (!leadToken || (status && status.booked)) return
    const id = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(id)
  }, [leadToken, status && status.booked]) // eslint-disable-line react-hooks/exhaustive-deps

  const firstCharge = pp.plans[plan] ? pp.plans[plan].firstCharge : 0
  const sumShares = Math.round(rows.reduce((a, r) => a + Number(r.share || 0), 0) * 100) / 100

  // Only the lead-facing responses carry each golfer's payToken; some responses
  // (e.g. the lead's own pay) may omit it. Keep any token we've already seen so
  // the "Copy link" buttons never flicker out.
  function applyStatus(next) {
    if (!next) return
    setStatus(prev => {
      if (!prev) return next
      const seen = {}; (prev.golfers || []).forEach(g => { if (g.payToken) seen[g.id] = g.payToken })
      return { ...next, golfers: (next.golfers || []).map(g => ({ ...g, payToken: g.payToken || seen[g.id] })) }
    })
  }
  async function copyLink(g) {
    const url = `${window.location.origin}/pay-share/${g.payToken}`
    try { await navigator.clipboard.writeText(url) } catch (_) { window.prompt(fr ? 'Copiez le lien :' : 'Copy this link:', url) }
    setCopiedId(g.id); setTimeout(() => setCopiedId(c => (c === g.id ? null : c)), 1800)
  }
  async function remindGolfer(g) {
    try {
      await tournamentApi.post(`/public/quote/${token}/golfers/${g.id}/remind`)
      setRemindedId(g.id); setTimeout(() => setRemindedId(c => (c === g.id ? null : c)), 2500)
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  function applyEven(list) { const shares = evenSplit(total, list.length); return list.map((r, i) => ({ ...r, share: shares[i] })) }
  function setCount(n) {
    n = Math.max(2, Math.min(64, n))
    setRows(prev => {
      let next = Array.from({ length: n }, (_, i) => prev[i] || { name: '', email: '', share: 0, isLead: false })
      if (!next.some(r => r.isLead)) next = next.map((r, i) => ({ ...r, isLead: i === 0 }))
      return shareMode === 'even' ? applyEven(next) : next
    })
  }
  const updateRow = (i, patch) => setRows(prev => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  async function startSplit() {
    setErr(null)
    for (const r of rows) { if (!r.name.trim() || !r.email.trim()) { setErr(fr ? "Chaque golfeur a besoin d'un nom et d'un courriel." : 'Each golfer needs a name and email.'); return } }
    if (shareMode === 'custom' && Math.abs(sumShares - total) > 0.01) { setErr((fr ? 'Les parts doivent totaliser ' : 'Shares must add up to ') + money(total, cur) + ' (' + money(sumShares, cur) + ')'); return }
    setBusy(true)
    try {
      const golfers = rows.map(r => ({ name: r.name.trim(), email: r.email.trim(), isLead: !!r.isLead, ...(shareMode === 'custom' ? { share: Number(r.share) } : {}) }))
      const res = await tournamentApi.post(`/public/quote/${token}/split/setup`, { plan, golfers })
      const d = res.data
      // A live price moved during the pre-check — show the new total, redo shares.
      if (d.priceChanged) {
        setErr(fr
          ? 'Le prix du voyage a été mis à jour. Veuillez actualiser la page pour voir le nouveau total, puis saisir de nouveau la part de chaque golfeur.'
          : 'The trip price has been updated. Please refresh the page to see the new total, then re-enter each golfer’s share.')
        setBusy(false); return
      }
      // A tee time we could not re-confirm on the live sheet — staff are notified;
      // nothing was charged. Never the scary "no longer available" dead-end.
      if (d.confirming || d.conflict || d.escalated) {
        setErr(fr
          ? `Nous confirmons l'heure de départ${d.courseName ? ' pour ' + d.courseName : ''} auprès du parcours — aucun montant n'a été facturé. Votre fournisseur de voyage vous contactera sous peu.`
          : `We're confirming the tee time${d.courseName ? ' for ' + d.courseName : ''} with the course — nothing has been charged. Your travel provider will follow up shortly.`)
        setBusy(false); return
      }
      setLeadToken(d.leadToken); applyStatus(d.split)
    } catch (e) { setErr(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }

  // Poll collection status until booked.
  useEffect(() => {
    if (!leadToken || (status && status.booked)) return
    let live = true
    const iv = setInterval(async () => {
      try {
        const r = await tournamentApi.get(`/public/quote/${token}/split/status`)
        if (!live) return
        if (r.data.split) applyStatus(r.data.split)
        if (r.data.split && r.data.split.booked) { clearInterval(iv); onBooked && onBooked() }
      } catch (_) { /* ignore */ }
    }, 5000)
    return () => { live = false; clearInterval(iv) }
  }, [leadToken, status && status.booked, token]) // eslint-disable-line react-hooks/exhaustive-deps

  const me = status ? (status.golfers || []).find(g => g.isLead) : null
  const myShare = me ? me.firstChargeShare : firstCharge

  async function pay(endpoint) {
    const c = { number: (card.number || '').trim(), expiry: (card.expiry || '').trim(), cvc: (card.cvc || '').trim() }
    if (!c.number || !c.expiry || !c.cvc) { setErr(fr ? 'Entrez les détails de la carte.' : 'Enter your card details.'); return }
    setBusy(true); setErr(null)
    try {
      const r = await tournamentApi.post(endpoint, { card: c })
      const d = r.data
      if (d.expired) { setErr(fr ? 'La garantie de prix a expiré.' : 'The price hold has expired.'); setBusy(false); return }
      setCard({ number: '', expiry: '', cvc: '' }); setCoverMode(false)
      if (d.split) applyStatus(d.split)
      if (d.booked) { onBooked && onBooked() }
    } catch (e) { setErr(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }
  const payMyShare = () => pay(`/public/quote/share/${leadToken}/pay`)
  const coverRemaining = () => pay(`/public/quote/${token}/split/cover-remaining`)
  async function extendHold() {
    setBusy(true); setExtendErr(null)
    try { const r = await tournamentApi.post(`/public/quote/${token}/split/extend`); if (r.data.split) applyStatus(r.data.split) }
    catch (e) { setExtendErr(e?.response?.data?.message || e.message) } finally { setBusy(false) }
  }

  const cardInputs = (
    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      <input value={card.number} onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/\D/g, '').slice(0, 19).replace(/(.{4})/g, '$1 ').trim() }))}
        inputMode="numeric" autoComplete="cc-number" placeholder={fr ? 'Numéro de carte' : 'Card number'}
        style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={card.expiry} onChange={e => { const d = e.target.value.replace(/\D/g, '').slice(0, 4); setCard(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }}
          inputMode="numeric" autoComplete="cc-exp" placeholder={fr ? 'MM / AA' : 'MM / YY'} style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
        <input value={card.cvc} onChange={e => setCard(c => ({ ...c, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
          inputMode="numeric" autoComplete="cc-csc" placeholder="CVC" style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
      </div>
    </div>
  )

  // ── SETUP phase: build the roster + shares.
  if (!leadToken || !status) {
    return (
      <div>
        {allowedPlans.length > 1 && (
          <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
            {allowedPlans.map(k => {
              const active = plan === k
              return (
                <button key={k} type="button" onClick={() => setPlan(k)}
                  style={{ textAlign: 'left', border: `1.5px solid ${active ? accent : '#e2e8f0'}`, background: active ? '#f8fafc' : '#fff', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{planMeta[k]}</span>
                    <span style={{ fontSize: 13, color: '#475569' }}>{money(pp.plans[k].firstCharge, cur)} {fr ? "aujourd'hui" : 'today'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{planSummary(k, pp.plans[k])}</div>
                </button>
              )
            })}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{fr ? 'Qui participe ?' : "Who's splitting?"}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" onClick={() => setCount(rows.length - 1)} disabled={rows.length <= 2} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 16, cursor: rows.length <= 2 ? 'default' : 'pointer' }}>−</button>
            <span style={{ fontSize: 14, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{rows.length}</span>
            <button type="button" onClick={() => setCount(rows.length + 1)} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 16, cursor: 'pointer' }}>+</button>
          </div>
        </div>
        <div style={{ display: 'flex', marginBottom: 10, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          {[['even', fr ? 'Parts égales' : 'Even split'], ['custom', fr ? 'Montants personnalisés' : 'Custom amounts']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => { setShareMode(k); if (k === 'even') setRows(applyEven(rows)) }}
              style={{ flex: 1, padding: '7px 8px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: shareMode === k ? '#f1f5f9' : '#fff', color: shareMode === k ? '#0f172a' : '#94a3b8' }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input value={r.name} onChange={e => updateRow(i, { name: e.target.value })} placeholder={fr ? 'Nom' : 'Name'}
                style={{ flex: 1.2, minWidth: 0, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              <input value={r.email} onChange={e => updateRow(i, { email: e.target.value })} placeholder={fr ? 'Courriel' : 'Email'} inputMode="email"
                style={{ flex: 1.6, minWidth: 0, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              {shareMode === 'custom' ? (
                <input value={r.share} onChange={e => updateRow(i, { share: e.target.value.replace(/[^\d.]/g, '') })} inputMode="decimal"
                  style={{ width: 78, padding: '8px 8px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, textAlign: 'right', boxSizing: 'border-box' }} />
              ) : (
                <span style={{ width: 78, textAlign: 'right', fontSize: 13, color: '#475569', fontWeight: 600 }}>{money(r.share, cur)}</span>
              )}
              {/* Fixed-width slot on EVERY row (lead or not) so the name/email/amount
                  columns line up; the organizer's chip is a soft outline, right-aligned. */}
              <span style={{ width: 46, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                {r.isLead && <span title={fr ? 'Organisateur' : 'Organizer'} style={{ fontSize: 10, fontWeight: 700, color: accent, background: 'transparent', border: `1px solid ${accent}`, padding: '1px 6px', borderRadius: 6, lineHeight: 1.4, whiteSpace: 'nowrap' }}>{fr ? 'VOUS' : 'YOU'}</span>}
              </span>
            </div>
          ))}
        </div>
        {shareMode === 'custom' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5, color: Math.abs(sumShares - total) > 0.01 ? '#b45309' : '#166534', fontWeight: 600 }}>
            <span>{fr ? 'Total des parts' : 'Shares total'}</span><span>{money(sumShares, cur)} / {money(total, cur)}</span>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
          {fr ? `Chaque golfeur reçoit un lien pour payer sa part (${money(firstCharge, cur)} au total aujourd'hui, réparti). Le voyage est réservé une fois que tout le monde a payé.`
              : `Each golfer gets a link to pay their share (${money(firstCharge, cur)} due today in total, split between you). The trip is booked once everyone has paid.`}
        </div>
        {err && <div style={{ marginTop: 10, color: '#991b1b', fontSize: 13 }}>{err}</div>}
        <button type="button" onClick={startSplit} disabled={busy}
          style={{ marginTop: 12, width: '100%', padding: '12px 16px', background: accent, color: onAccent, border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
          {busy ? (fr ? 'Envoi…' : 'Sending…') : (fr ? 'Envoyer les invitations et continuer' : 'Send invites & continue')}
        </button>
      </div>
    )
  }

  // ── COLLECTING phase: progress + roster + lead pay / cover / extend.
  const golfers = status.golfers || []
  const pct = status.totalGolfers ? Math.round((status.authorizedCount / status.totalGolfers) * 100) : 0
  // Prices are held until a concrete DATE (~48h out), shown to the group — not a
  // minutes countdown. Expired once the server says so OR the hold date passes
  // (the server rejects any charge past it anyway).
  const heldUntilMs = status.holdExpiresAtMs || null
  const expiredNow = status.status === 'expired' || (heldUntilMs != null && heldUntilMs <= now) || cdExpired
  const extRemaining = Math.max(0, (status.maxExtends || 0) - (status.extendCount || 0))
  const canExtend = extRemaining > 0 && !expiredNow
  return (
    <div>
      {expiredNow ? (
        <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 13.5, color: '#9a3412', lineHeight: 1.5, marginBottom: 12 }}>
          {fr ? "La garantie de prix a expiré et les autorisations ont été libérées. Personne n'a été facturé. Vous pouvez relancer un partage." : 'The price hold expired and any holds were released. No one was charged. You can start a new split.'}
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
            <span>{fr ? 'Paiements' : 'Payments'}</span><span>{status.authorizedCount} / {status.totalGolfers} {fr ? 'payés' : 'paid'}</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: pct + '%', background: accent, transition: 'width 0.3s' }} />
          </div>
          {/* Live price-hold countdown (shown on both the lead screen + the golfer
              pay-share page). The lead can push the deadline out below it. */}
          {heldUntilMs && <SplitCountdown expiresAtMs={heldUntilMs} fr={fr} onExpire={() => setCdExpired(true)} />}
          {heldUntilMs && (canExtend || extendErr) && (
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              {canExtend && (
                <>
                  <button type="button" onClick={extendHold} disabled={busy}
                    style={{ width: '100%', padding: '9px 12px', background: '#fff', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
                    {busy ? (fr ? 'Vérification de la disponibilité…' : 'Checking availability…') : (fr ? 'Prolonger le délai' : 'Extend the hold')}
                  </button>
                  <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>{fr ? "nous confirmons d'abord que les départs sont toujours disponibles" : "we'll confirm the tee times are still available first"}</div>
                </>
              )}
              {extendErr && <div style={{ marginTop: canExtend ? 8 : 0, color: '#991b1b', fontSize: 12 }}>{extendErr}</div>}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {golfers.map(g => (
          <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid #f1f5f9', borderRadius: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a' }}>{g.name}{g.isLead ? (fr ? ' (vous)' : ' (you)') : ''}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{money(g.firstChargeShare, cur)} {fr ? "aujourd'hui" : 'today'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {!expiredNow && !g.isLead && !g.paid && !g.coveredByLead && (
                <button type="button" onClick={() => remindGolfer(g)} title={fr ? 'Renvoyer l’invitation à payer' : 'Re-send their pay invite'}
                  style={{ padding: '4px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, border: `1px solid ${remindedId === g.id ? '#166534' : '#cbd5e1'}`, background: '#fff', color: remindedId === g.id ? '#166534' : '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {remindedId === g.id ? (fr ? 'Rappelé !' : 'Reminded!') : (fr ? 'Rappeler' : 'Remind')}
                </button>
              )}
              {!expiredNow && !g.isLead && !g.paid && g.payToken && (
                <button type="button" onClick={() => copyLink(g)} title={fr ? 'Copier le lien de paiement' : 'Copy payment link'}
                  style={{ padding: '4px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, border: `1px solid ${copiedId === g.id ? '#166534' : accent}`, background: '#fff', color: copiedId === g.id ? '#166534' : accent, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {copiedId === g.id ? (fr ? 'Copié !' : 'Copied!') : (fr ? 'Copier le lien' : 'Copy link')}
                </button>
              )}
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, color: g.paid ? '#166534' : '#92400e', background: g.paid ? '#dcfce7' : '#fef3c7' }}>
                {g.coveredByLead ? (fr ? 'Couvert' : 'Covered') : g.paid ? (fr ? 'Payé' : 'Paid') : (fr ? 'En attente' : 'Pending')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {!expiredNow && me && !me.paid && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{fr ? 'Payez votre part' : 'Pay your share'} — {money(myShare, cur)}</div>
          {cardInputs}
          {err && <div style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>{err}</div>}
          <button type="button" onClick={payMyShare} disabled={busy}
            style={{ marginTop: 10, width: '100%', padding: '11px 16px', background: accent, color: onAccent, border: 'none', borderRadius: 8, fontSize: 14.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? (fr ? 'Traitement…' : 'Processing…') : `${fr ? 'Payer' : 'Pay'} ${money(myShare, cur)}`}
          </button>
        </div>
      )}

      {!expiredNow && me && me.paid && !status.booked && status.authorizedCount < status.totalGolfers && (
        <div>
          <div style={{ padding: '9px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
            {fr ? "Votre part est payée. En attente des autres golfeurs — le voyage sera réservé dès que tout le monde aura payé." : "Your share is paid. Waiting on the other golfers — the trip books as soon as everyone has paid."}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setCoverMode(v => !v)} disabled={busy}
              style={{ flex: 1, minWidth: 130, padding: '9px 12px', background: coverMode ? accent : '#fff', color: coverMode ? onAccent : '#334155', border: `1px solid ${coverMode ? accent : '#e2e8f0'}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
              {fr ? 'Couvrir le reste' : 'Cover the rest'}
            </button>
          </div>
          {coverMode && (
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12, marginTop: 10 }}>
              <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>
                {fr ? "Payez les parts des golfeurs qui n'ont pas encore payé sur votre carte pour réserver dès maintenant. Vous prendrez aussi en charge leurs paiements futurs." : "Pay the unpaid golfers' shares on your card to book now. You'll also take on their future payments."}
              </div>
              {cardInputs}
              {err && <div style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>{err}</div>}
              <button type="button" onClick={coverRemaining} disabled={busy}
                style={{ marginTop: 10, width: '100%', padding: '11px 16px', background: accent, color: onAccent, border: 'none', borderRadius: 8, fontSize: 14.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? (fr ? 'Traitement…' : 'Processing…') : (fr ? 'Couvrir et réserver' : 'Cover & book')}
              </button>
            </div>
          )}
          {err && !coverMode && <div style={{ marginTop: 8, color: '#991b1b', fontSize: 13 }}>{err}</div>}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{fr ? 'Sécurisé par InteGolf · carte traitée via Moneris.' : 'Secured by InteGolf · card processed through Moneris.'}</div>
    </div>
  )
}
